import React, { useState } from 'react';
import { Calculator, Copy, Trash2, MousePointer, DollarSign, Info } from 'lucide-react';

const CpcCalculatorTool = () => {
  const [totalCost, setTotalCost] = useState('');
  const [clicks, setClicks] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const cost = parseFloat(totalCost);
    const numClicks = parseFloat(clicks);

    if (isNaN(cost) || isNaN(numClicks) || cost < 0 || numClicks <= 0) {
      setResult(null);
      return;
    }

    // CPC = Total Cost / Number of Clicks
    const cpc = cost / numClicks;
    const clicksPerDollar = numClicks / cost;

    setResult({
      totalCost: cost,
      clicks: numClicks,
      cpc,
      clicksPerDollar,
      costPer100Clicks: cpc * 100
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setTotalCost('');
    setClicks('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Determine CPC rating
  const getCpcRating = (cpc) => {
    if (cpc < 0.5) return { label: 'Excellent', color: 'text-green-400' };
    if (cpc < 1) return { label: 'Good', color: 'text-blue-400' };
    if (cpc < 2) return { label: 'Average', color: 'text-yellow-400' };
    if (cpc < 5) return { label: 'Above Average', color: 'text-orange-400' };
    return { label: 'High', color: 'text-red-400' };
  };

  const benchmarks = [
    { platform: 'Google Search', range: '$1 - $5' },
    { platform: 'Google Display', range: '$0.50 - $2' },
    { platform: 'Facebook/Instagram', range: '$0.50 - $2' },
    { platform: 'LinkedIn', range: '$2 - $7' },
    { platform: 'Twitter/X', range: '$0.50 - $3' },
    { platform: 'TikTok', range: '$0.50 - $2' },
  ];

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <DollarSign size={18} className="text-green-400" />
          <h3 className="text-sm font-medium text-gray-300">Campaign Data</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Cost ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              placeholder="e.g., 500"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="total-cost-input"
            />
            <p className="text-xs text-gray-500 mt-1">Total ad spend for the campaign</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Clicks
            </label>
            <input
              type="number"
              value={clicks}
              onChange={(e) => setClicks(e.target.value)}
              placeholder="e.g., 1000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="clicks-input"
            />
            <p className="text-xs text-gray-500 mt-1">Number of ad clicks received</p>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-cpc-btn"
      >
        <Calculator size={18} />
        Calculate CPC
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="cpc-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MousePointer size={18} className="text-blue-400" />
                <span className="text-sm text-gray-400">Cost Per Click (CPC)</span>
              </div>
              <button
                onClick={() => copyResult(result.cpc, 'cpc')}
                className={`p-1.5 rounded transition-colors ${
                  copied === 'cpc' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-cpc"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="text-4xl font-bold text-green-400" data-testid="cpc-result">
              {formatCurrency(result.cpc)}
            </div>
            <div className={`text-sm mt-1 ${getCpcRating(result.cpc).color}`}>
              {getCpcRating(result.cpc).label} CPC
            </div>
          </div>

          {/* Secondary Metrics */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Clicks per $1 Spend</div>
              <div className="text-lg font-bold text-blue-400">
                {result.clicksPerDollar.toFixed(2)} clicks
              </div>
            </div>
            <div className="p-3 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Cost per 100 Clicks</div>
              <div className="text-lg font-bold text-purple-400">
                {formatCurrency(result.costPer100Clicks)}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Cost</span>
                <span className="text-white font-medium">{formatCurrency(result.totalCost)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Clicks</span>
                <span className="text-white font-medium">{formatNumber(result.clicks)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">CPC (Cost ÷ Clicks)</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.cpc)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* What is CPC */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Info size={18} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">What is CPC?</h3>
        </div>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            <span className="text-white font-medium">Cost Per Click (CPC)</span> measures how much you pay 
            each time someone clicks on your ad. It's one of the most important metrics in pay-per-click 
            (PPC) advertising.
          </p>
          <div className="p-3 bg-gray-900 rounded">
            <div className="text-xs text-gray-500 mb-2">Formula:</div>
            <div className="font-mono text-yellow-400">
              CPC = Total Ad Cost ÷ Number of Clicks
            </div>
          </div>
          <p>
            <span className="text-white font-medium">Why CPC matters:</span> Lower CPC means you get more 
            clicks for your budget, potentially leading to more conversions. However, quality of clicks 
            matters too - cheap clicks that don't convert aren't valuable.
          </p>
        </div>
      </div>

      {/* Industry Benchmarks */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-4">Average CPC by Platform</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {benchmarks.map((item, index) => (
            <div key={index} className="flex justify-between items-center p-2 bg-gray-900 rounded">
              <span className="text-sm text-gray-400">{item.platform}</span>
              <span className="text-sm font-medium text-green-400">{item.range}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Note: CPC varies significantly based on industry, targeting, competition, and ad quality. 
          Highly competitive industries like legal, insurance, and finance often have much higher CPCs.
        </p>
      </div>

      {/* Related Metrics */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Related Advertising Metrics</h3>
        <div className="text-sm text-gray-400 space-y-2">
          <div><span className="text-white">CPM</span> - Cost Per 1000 Impressions: Cost for visibility</div>
          <div><span className="text-white">CPA</span> - Cost Per Acquisition: Cost per conversion</div>
          <div><span className="text-white">CTR</span> - Click-Through Rate: Clicks divided by impressions</div>
          <div><span className="text-white">ROAS</span> - Return On Ad Spend: Revenue divided by ad cost</div>
        </div>
      </div>

      {/* Tips */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Tips to Lower CPC</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• <span className="text-white">Improve Quality Score</span> - Better ads get lower CPCs</div>
          <div>• <span className="text-white">Use negative keywords</span> - Avoid irrelevant clicks</div>
          <div>• <span className="text-white">Test ad variations</span> - Find what resonates best</div>
          <div>• <span className="text-white">Refine targeting</span> - Focus on high-intent audiences</div>
          <div>• <span className="text-white">Optimize landing pages</span> - Better relevance = lower costs</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-cpc-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CpcCalculatorTool;
